import { motion } from "framer-motion";
import { BookOpen, ExternalLink } from "lucide-react";
import type { ReactNode } from "react";
import type { ChatMessage, CitationLink } from "@/types";

// ── Inline citation rendering ────────────────────────────────────

const CITE_RE = /\[([^\]]+?\d{4}[^\]]*?)\]/g;

function renderTextWithCitations(
  text: string,
  citationLinks?: CitationLink[],
): ReactNode[] {
  if (!citationLinks?.length) return [text];

  const urlMap = new Map(citationLinks.map((c) => [c.cite, c.url]));
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  CITE_RE.lastIndex = 0;
  while ((match = CITE_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const cite = match[1];
    const url = urlMap.get(cite);

    if (url) {
      parts.push(
        <a
          key={`cite-${match.index}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-baseline gap-0.5 rounded-md bg-slate-200/60 px-1 py-0.5 text-[13px] font-medium text-navy no-underline transition hover:bg-green/20 hover:text-navy"
        >
          {cite}
          <ExternalLink className="relative top-px size-2.5 shrink-0 opacity-50" />
        </a>,
      );
    } else {
      parts.push(
        <span
          key={`cite-${match.index}`}
          className="rounded-md bg-slate-200/60 px-1 py-0.5 text-[13px] font-medium text-slate-600"
        >
          {cite}
        </span>,
      );
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

// ── Sources footer ───────────────────────────────────────────────

function CitationSources({ links }: { links: CitationLink[] }) {
  return (
    <div className="mt-3 border-t border-slate-200/60 pt-2.5">
      <div className="mb-1.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        <BookOpen className="size-3" />
        Sources
      </div>
      <div className="flex flex-col gap-1">
        {links.map((link) => (
          <a
            key={link.cite}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-1.5 rounded-lg px-1.5 py-1 text-[13px] leading-tight text-slate-600 transition hover:bg-green/10 hover:text-navy"
          >
            <ExternalLink className="size-3 shrink-0 opacity-40 group-hover:opacity-70" />
            <span className="font-medium">{link.cite}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

// ── Message bubble ───────────────────────────────────────────────

export function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.sender === "user";
  const hasCitations = !!message.citationLinks?.length;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.24, ease: "easeOut" }}
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
    >
      <div
        className={`max-w-[84%] rounded-3xl px-4 py-3 text-[15px] leading-6 ${
          isUser
            ? "rounded-br-lg bg-bubble-user text-white"
            : "rounded-bl-lg bg-bubble-ai text-bubble-ai-text"
        }`}
      >
        {!isUser && (
          <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-muted">
            <img src="/brain-9-svgrepo-com.svg" alt="" className="size-3.5" />
            CheckCells AI
          </div>
        )}

        <span>
          {isUser
            ? message.text
            : renderTextWithCitations(message.text, message.citationLinks)}
        </span>

        {hasCitations && <CitationSources links={message.citationLinks!} />}
      </div>
    </motion.div>
  );
}

// ── Typing indicator ─────────────────────────────────────────────

export function TypingIndicator() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="flex"
    >
      <div className="rounded-3xl rounded-bl-lg bg-bubble-ai px-4 py-3">
        <div className="flex gap-1.5">
          {[0, 1, 2].map((dot) => (
            <motion.span
              key={dot}
              className="size-2 rounded-full bg-slate-300"
              animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
              transition={{
                duration: 0.85,
                repeat: Infinity,
                delay: dot * 0.12,
              }}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
}
